import React, { useState } from 'react';
import Footer from '../components/Footer';
import SEOHead from '../seo/SEOHead';
import { PAGE_SEO } from '../seo/seoConfig';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    interest: '',
    message: '',
    consent: false
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validateForm = (data = formData) => {
    const newErrors = {};

    if (!data.firstName.trim()) {
      newErrors.firstName = 'First name is required.';
    }
    if (!data.lastName.trim()) {
      newErrors.lastName = 'Last name is required.';
    }
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!data.phoneNumber.trim() || !/^\d{10}$/.test(data.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit mobile number.';
    }
    if (!data.interest) {
      newErrors.interest = 'Please select an interest.';
    }
    if (!data.message.trim()) {
      newErrors.message = 'Please enter your message.';
    }
    if (!data.consent) {
      newErrors.consent = 'You must agree to the Terms & Conditions and Privacy Policy to proceed.';
    }

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const executeSubmit = () => {
    setSubmitted(true);
    console.log('Form submitted successfully:', formData);
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }
    executeSubmit();
    return true;
  };

  return (
    <>
      <SEOHead
        title={PAGE_SEO.contact.title}
        description={PAGE_SEO.contact.description}
        keywords={PAGE_SEO.contact.keywords}
        canonical={PAGE_SEO.contact.canonical}
        ogImage={PAGE_SEO.contact.ogImage}
      />
      <div className="contact-us-page">
        <style>{`
          .contact-us-page {
            font-family: var(--font-family-primary);
            background-color: #f8f9fa;
            min-height: 100vh;
            padding: 0;
          }

          .contact-content {
            max-width: 1200px;
            margin: 0 auto;
            padding: 64px 24px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 64px;
            align-items: start;
          }

          .left-section {
            padding-right: 32px;
          }

          .contact-title {
            font-size: var(--font-size-5);
            font-weight: 500;
            color: var(--color-1);
            margin-bottom: 24px;
            line-height: 1.2;
          }

          .contact-description {
            font-size: var(--font-size-2);
            color: #666;
            margin-bottom: 48px;
            line-height: 1.6;
          }

          .contact-info {
            display: flex;
            flex-direction: column;
            gap: 32px;
          }

          .contact-item {
            display: flex;
            align-items: flex-start;
            gap: 16px;
          }

          .contact-icon {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background-color: var(--color-1);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            flex-shrink: 0;
          }

          .contact-details {
            flex: 1;
          }

          .contact-item-title {
            font-size: var(--font-size-2);
            font-weight: 500;
            color: #333;
            margin-bottom: 4px;
          }

          .contact-text {
            font-size: var(--font-size-2);
            color: #666;
            line-height: 1.5;
          }

          .right-section {
            background-color: white;
            padding: 32px;
            border-radius: 8px;
            border: 2px solid var(--color-1);
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 16px;
          }

          .input-group {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .input-group.full-width {
            margin-bottom: 16px;
          }

          .form-input {
            width: 100%;
            padding: 12px 16px;
            font-size: var(--font-size-2);
            border: 1px solid #ddd;
            background-color: #f8f9fa;
            outline: none;
            transition: border-color 0.2s;
            font-family: inherit;
            border-radius: 4px;
          }

          .form-input:focus {
            border-color: var(--color-1);
          }

          .form-input-full {
            width: 100%;
            padding: 12px 16px;
            font-size: var(--font-size-2);
            border: 1px solid #ddd;
            background-color: #f8f9fa;
            outline: none;
            transition: border-color 0.2s;
            font-family: inherit;
            border-radius: 4px;
          }

          .form-input-full:focus {
            border-color: var(--color-1);
          }

          .phone-container {
            display: flex;
          }

          .country-code {
            padding: 12px 16px;
            background-color: #f8f9fa;
            border: 1px solid #ddd;
            border-right: none;
            border-radius: 4px 0 0 4px;
            font-size: var(--font-size-2);
            color: #666;
            font-weight: 500;
          }

          .phone-input {
            flex: 1;
            padding: 12px 16px;
            font-size: var(--font-size-2);
            border: 1px solid #ddd;
            border-radius: 0 4px 4px 0;
            background-color: #f8f9fa;
            outline: none;
            transition: border-color 0.2s;
            font-family: inherit;
          }

          .phone-input:focus {
            border-color: var(--color-1);
          }

          .form-select {
            width: 100%;
            padding: 12px 16px;
            font-size: var(--font-size-2);
            border: 1px solid #ddd;
            background-color: #f8f9fa;
            outline: none;
            font-family: inherit;
            border-radius: 4px;
          }

          .form-select:focus {
            border-color: var(--color-1);
          }

          .form-textarea {
            width: 100%;
            padding: 12px 16px;
            font-size: var(--font-size-2);
            border: 1px solid #ddd;
            background-color: #f8f9fa;
            outline: none;
            resize: vertical;
            font-family: inherit;
            border-radius: 4px;
          }

          .form-textarea:focus {
            border-color: var(--color-1);
          }

          .input-error {
            border-color: #dc2626 !important;
            background-color: #fef2f2 !important;
          }

          .error-text {
            color: #dc2626;
            font-size: 13px;
            margin-top: 2px;
          }

          /* Consent Checkbox Styling */
          .consent-wrapper {
            display: flex;
            flex-direction: column;
            gap: 4px;
            background-color: #f8f9fa;
            padding: 14px 16px;
            border-radius: 6px;
            border: 1px solid #ddd;
            margin-bottom: 20px;
          }

          .consent-error-box {
            border-color: #dc2626;
            background-color: #fef2f2;
          }

          .consent-label {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            cursor: pointer;
          }

          .consent-checkbox {
            width: 18px;
            height: 18px;
            margin-top: 2px;
            accent-color: var(--color-1);
            cursor: pointer;
            flex-shrink: 0;
          }

          .consent-text {
            font-size: 13.5px;
            color: #555;
            line-height: 1.5;
          }

          .legal-link {
            color: var(--color-1);
            font-weight: 600;
            text-decoration: none;
            transition: color 0.2s;
          }

          .legal-link:hover {
            text-decoration: underline;
            color: #e06b00;
          }

          .required-star {
            color: #dc2626;
            font-weight: bold;
          }

          .submit-button {
            width: 100%;
            padding: 16px;
            background-color: var(--color-1);
            color: white;
            border: none;
            font-size: var(--font-size-2);
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.2s, transform 0.1s;
            font-family: inherit;
            border-radius: 4px;
          }

          .submit-button:hover {
            background-color: #e06b00;
          }

          .submit-button:active {
            transform: scale(0.99);
          }

          .contact-success-msg {
            text-align: center;
            padding: 32px 16px;
          }

          .contact-success-msg h3 {
            font-size: 22px;
            color: var(--color-1);
            margin-bottom: 12px;
          }

          .contact-success-msg p {
            font-size: 15px;
            color: #555;
            margin-bottom: 24px;
            line-height: 1.6;
          }

          .map-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 24px 64px;
          }

          .map-section {
            background-color: white;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            height: 450px;
            border-radius: 8px;
          }

          .map-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px 20px;
            background-color: #f8f9fa;
            border-bottom: 1px solid #ddd;
          }

          .coordinates {
            font-size: var(--font-size-2);
            font-weight: 500;
            color: #333;
          }

          .map-actions {
            display: flex;
            gap: 12px;
          }

          .map-button {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            background-color: transparent;
            border: none;
            font-size: var(--font-size-1);
            color: #666;
            cursor: pointer;
            transition: background-color 0.2s;
            font-family: inherit;
            border-radius: 4px;
            text-decoration: none;
          }

          .map-button:hover {
            background-color: #e9ecef;
          }

          .embedded-map {
            width: 100%;
            height: 400px;
            border: none;
          }

          /* Mobile Styles */
          @media (max-width: 768px) {
            .contact-content {
              grid-template-columns: 1fr;
              gap: 32px;
              padding: 32px 16px;
            }

            .left-section {
              padding-right: 0;
            }

            .contact-title {
              font-size: 2rem;
              text-align: center;
            }

            .contact-description {
              text-align: center;
              margin-bottom: 32px;
            }

            .contact-info {
              gap: 24px;
            }

            .contact-icon {
              width: 40px;
              height: 40px;
            }

            .contact-item-title {
              font-size: 1rem;
            }

            .contact-text {
              font-size: 0.9rem;
            }

            .right-section {
              padding: 24px;
              border-radius: 8px;
            }

            .form-row {
              grid-template-columns: 1fr;
              gap: 16px;
            }

            .map-container {
              padding: 0 16px 32px;
            }

            .map-section {
              height: 350px;
            }

            .map-header {
              flex-direction: column;
              gap: 12px;
              padding: 12px;
              text-align: center;
            }

            .coordinates {
              font-size: 0.9rem;
            }

            .map-actions {
              justify-content: center;
            }

            .map-button {
              padding: 6px 10px;
              font-size: 0.8rem;
            }

            .embedded-map {
              height: 280px;
            }
          }

          /* Small mobile screens */
          @media (max-width: 480px) {
            .contact-content {
              padding: 24px 12px;
            }

            .contact-title {
              font-size: 1.75rem;
            }

            .right-section {
              padding: 20px;
            }

            .form-input,
            .form-input-full,
            .phone-input,
            .form-select,
            .form-textarea {
              padding: 10px 12px;
            }

            .submit-button {
              padding: 14px;
            }

            .map-container {
              padding: 0 12px 24px;
            }

            .map-section {
              height: 300px;
            }

            .embedded-map {
              height: 240px;
            }
          }
        `}</style>

        <SEOHead
          title={PAGE_SEO.contact.title}
          description={PAGE_SEO.contact.description}
          keywords={PAGE_SEO.contact.keywords}
          canonical={PAGE_SEO.contact.canonical}
          ogImage={PAGE_SEO.contact.ogImage}
        />
        <div className="contact-content">
          <div className="left-section">
            <h1 className="contact-title">Get in Touch!</h1>
            <p className="contact-description">
              We'd love to hear from you. Whether you have<br />
              a question, need support, or want to discuss
            </p>

            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div className="contact-details">
                  <h3 className="contact-item-title">Phone</h3>
                  <p className="contact-text">+91 73107 77481</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div className="contact-details">
                  <h3 className="contact-item-title">Email</h3>
                  <p className="contact-text"> info@realflame.in</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div className="contact-details">
                  <h3 className="contact-item-title">Address</h3>
                  <p className="contact-text">
                    Plot No. 12, Sector – IIDC<br />
                    Haridwar, Uttarakhand 249403
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="right-section">
            {submitted ? (
              <div className="contact-success-msg">
                <h3>Thank you for reaching out!</h3>
                <p>Your message has been submitted successfully. We will get back to you soon.</p>
                <button 
                  type="button" 
                  className="submit-button"
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      firstName: '',
                      lastName: '',
                      email: '',
                      phoneNumber: '',
                      interest: '',
                      message: '',
                      consent: false
                    });
                    setErrors({});
                  }}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="form-row">
                  <div className="input-group">
                    <input
                      type="text"
                      name="firstName"
                      placeholder="Enter first name *"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`form-input ${errors.firstName ? 'input-error' : ''}`}
                    />
                    {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                  </div>
                  <div className="input-group">
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Enter last name *"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`form-input ${errors.lastName ? 'input-error' : ''}`}
                    />
                    {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                  </div>
                </div>

                <div className="input-group full-width">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter email *"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input-full ${errors.email ? 'input-error' : ''}`}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className="input-group full-width">
                  <div className="phone-container">
                    <div className="country-code">+91</div>
                    <input
                      type="tel"
                      name="phoneNumber"
                      placeholder="Enter 10-digit phone number *"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      maxLength={10}
                      className={`phone-input ${errors.phoneNumber ? 'input-error' : ''}`}
                    />
                  </div>
                  {errors.phoneNumber && <span className="error-text">{errors.phoneNumber}</span>}
                </div>

                <div className="input-group full-width">
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleInputChange}
                    className={`form-select ${errors.interest ? 'input-error' : ''}`}
                  >
                    <option value="">Select Interest *</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Support</option>
                    <option value="business">Business</option>
                    <option value="partnership">Partnership</option>
                  </select>
                  {errors.interest && <span className="error-text">{errors.interest}</span>}
                </div>

                <div className="input-group full-width">
                  <textarea
                    name="message"
                    placeholder="How can we help? *"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`form-textarea ${errors.message ? 'input-error' : ''}`}
                    rows="4"
                  />
                  {errors.message && <span className="error-text">{errors.message}</span>}
                </div>

                {/* Mandatory Consent Checkbox with Auto-Submit on Terms Link Click */}
                <div className={`consent-wrapper ${errors.consent ? 'consent-error-box' : ''}`}>
                  <label className="consent-label" htmlFor="contact-consent">
                    <input
                      id="contact-consent"
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleInputChange}
                      className="consent-checkbox"
                    />
                    <span className="consent-text">
                      I have read and agree to the{' '}
                      <a 
                        href="/PrivacyPolicy" 
                        target="_self" 
                        rel="noopener noreferrer"
                        className="legal-link"
                      >
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a 
                        href="/TermsAndConditions" 
                         target="_self" 
                        className="legal-link"
                        title="Clicking Terms & Conditions will auto-submit the form if all fields are valid"
                      >
                        Terms & Conditions
                      </a>{' '}
                      and consent to receive communications from RealFlame through WhatsApp, SMS, Email, and Phone.{' '}
                      <span className="required-star">*</span>
                    </span>
                  </label>
                  {errors.consent && <span className="error-text">{errors.consent}</span>}
                </div>

                <button type="submit" className="submit-button">
                  SUBMIT
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="map-container">
          <div className="map-section">
            <div className="map-header">
              <div className="coordinates">Plot No. 12, Sector IIDC, Haridwar, Uttarakhand</div>
              <div className="map-actions">
                <a 
                  href="https://www.google.com/maps/dir//Plot+No.+12,+Sector+IIDC,+Haridwar,+Uttarakhand+249403,+India"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3 8-8"></path>
                    <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.59 0 3.08.41 4.37 1.13"></path>
                  </svg>
                  Directions
                </a>
                <a 
                  href="https://www.google.com/maps/place/Plot+No.+12,+Sector+IIDC,+Haridwar,+Uttarakhand+249403,+India"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
                  </svg>
                  View on Maps
                </a>
              </div>
            </div>
            <iframe
              className="embedded-map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3462.5844!2d78.1642!3d29.9457!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390eb528c3000001%3A0x1!2sPlot%20No.%2012%2C%20Sector%20IIDC%2C%20Haridwar%2C%20Uttarakhand%20249403!5e0!3m2!1sen!2sin!4v1640000000000!5m2!1sen!2sin"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Location Map - Plot No. 12, Sector IIDC, Haridwar, Uttarakhand"
            ></iframe>
          </div>
        </div>
      </div>
      <Footer/>
    </>
  );
};

export default ContactUs;